<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import MessageMeta from '../MessageMeta.vue';
import FormattedContent from './Text/FormattedContent.vue';
import WhatsAppPaymentOrderCard from './WhatsAppPaymentOrderCard.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';
import { MESSAGE_VARIANTS, ORIENTATION } from '../constants.js';
import MessageApi from 'dashboard/api/inbox/message';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';

const {
  content,
  contentAttributes,
  additionalAttributes,
  attachments,
  whatsappOrderStatus,
  whatsappPaymentStatus,
  whatsappPaymentOrder,
  conversationId,
  id,
  variant,
  orientation,
} = useMessageContext();

const { t } = useI18n();
const $store = useStore();
const templateInfo = computed(
  () => contentAttributes.value?.templateInfo || {}
);

const header = computed(() => templateInfo.value.header || null);
const footer = computed(() => templateInfo.value.footer || '');
const buttons = computed(() => templateInfo.value.buttons || []);

const hasMediaHeader = computed(() => {
  if (!header.value) return false;
  return ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(header.value.format);
});

const hasTextHeader = computed(() => {
  return header.value?.format === 'TEXT' && header.value?.text;
});

const mediaUrl = computed(() => header.value?.mediaUrl || '');

const normalizeButtonType = button =>
  String(button?.type || '')
    .toUpperCase()
    .replace(/-/g, '_');

// Separate CTA buttons (URL, PHONE, FLOW, COPY_CODE) from Quick Reply buttons
const ctaButtons = computed(() =>
  buttons.value.filter(b =>
    [
      'URL',
      'PHONE_NUMBER',
      'FLOW',
      'COPY_CODE',
      'VOICE_CALL',
      'CALL_PERMISSION_REQUEST',
    ].includes(normalizeButtonType(b))
  )
);

const firstPresent = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== '');

const readPath = (object, path) => {
  if (!object) return undefined;
  return path.split('.').reduce((currentValue, key) => {
    if (currentValue === undefined || currentValue === null) return undefined;
    return currentValue[key];
  }, object);
};

const normalizeList = value => {
  if (Array.isArray(value)) return value;
  return value ? [value] : [];
};

const normalizeAmount = amount => {
  if (amount && typeof amount === 'object') {
    return firstPresent(amount.value, amount.amount, amount.cents);
  }
  return amount;
};

const normalizePaymentMethod = type => {
  const normalized = String(type || '')
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .toLowerCase();

  if (normalized === 'pix' || normalized === 'pix_dynamic_code') {
    return 'pix_dynamic_code';
  }
  if (normalized === 'boleto') return 'boleto';
  if (normalized === 'link' || normalized === 'payment_link') {
    return 'payment_link';
  }
  return normalized;
};

const orderDetailsButton = computed(() => {
  const templateButton = buttons.value.find(
    b => b.type === 'ORDER_DETAILS' || b.type === 'order_details'
  );
  const interactiveParams =
    readPath(contentAttributes.value, 'interactive.action.parameters') || {};
  const persistedOrder = whatsappPaymentOrder?.value || {};

  // The template_info buttons from Meta sync only contain display metadata.
  // Runtime values are stored either in processed template params or in the interactive payload.
  const templateParams =
    additionalAttributes.value?.template_params ||
    additionalAttributes.value?.templateParams;
  const processedParams =
    templateParams?.processed_params || templateParams?.processedParams;
  const processedButtons = processedParams?.buttons;
  const processedButton = Array.isArray(processedButtons)
    ? processedButtons.find(
        b => b && (b.type === 'ORDER_DETAILS' || b.type === 'order_details')
      )
    : null;

  const mergedButton = {
    ...(templateButton || {}),
    ...(processedButton || {}),
  };
  const referenceId = firstPresent(
    mergedButton.reference_id,
    mergedButton.referenceId,
    interactiveParams.reference_id,
    interactiveParams.referenceId,
    persistedOrder.reference_id,
    persistedOrder.referenceId
  );

  if (!templateButton && !processedButton && !referenceId) return null;

  const paymentSettings = normalizeList(
    firstPresent(
      mergedButton.payment_settings,
      mergedButton.paymentSettings,
      interactiveParams.payment_settings,
      interactiveParams.paymentSettings
    )
  );

  return {
    ...mergedButton,
    reference_id: referenceId,
    total_amount: normalizeAmount(
      firstPresent(
        mergedButton.total_amount,
        mergedButton.totalAmount,
        interactiveParams.total_amount,
        interactiveParams.totalAmount,
        persistedOrder.total_amount,
        persistedOrder.totalAmount
      )
    ),
    order_status: firstPresent(
      whatsappOrderStatus?.value,
      persistedOrder.order_status,
      persistedOrder.orderStatus,
      mergedButton.order_status,
      mergedButton.orderStatus,
      readPath(interactiveParams, 'order.status')
    ),
    payment_status: firstPresent(
      whatsappPaymentStatus?.value,
      persistedOrder.payment_status,
      persistedOrder.paymentStatus,
      mergedButton.payment_status,
      mergedButton.paymentStatus,
      readPath(interactiveParams, 'payment.status')
    ),
    payment_settings: paymentSettings,
    payment_methods:
      persistedOrder.payment_methods ||
      persistedOrder.paymentMethods ||
      paymentSettings.map(setting => normalizePaymentMethod(setting.type)),
  };
});

const isUpdatingStatus = ref(false);

const updateOrderStatus = async (orderStatus, paymentStatus = null) => {
  const referenceId = orderDetailsButton.value?.reference_id;
  if (!referenceId || isUpdatingStatus.value) return;

  isUpdatingStatus.value = true;
  try {
    const response = await MessageApi.updateOrderStatus(
      conversationId.value,
      id.value,
      referenceId,
      orderStatus,
      paymentStatus
    );
    if (response.data && response.data.message) {
      $store.dispatch('updateMessage', response.data.message);
    }
    useAlert(t('CONVERSATION.WHATSAPP_TEMPLATE.ORDER_STATUS_UPDATED'));
  } catch (error) {
    useAlert(t('CONVERSATION.WHATSAPP_TEMPLATE.ORDER_STATUS_ERROR'));
  } finally {
    isUpdatingStatus.value = false;
  }
};

const quickReplyButtons = computed(() =>
  buttons.value.filter(b => normalizeButtonType(b) === 'QUICK_REPLY')
);

const ctaButtonIcon = button => {
  const icons = {
    URL: 'i-lucide-external-link',
    PHONE_NUMBER: 'i-lucide-phone',
    COPY_CODE: 'i-lucide-copy',
    FLOW: 'i-lucide-git-branch',
    VOICE_CALL: 'i-lucide-phone-call',
    CALL_PERMISSION_REQUEST: 'i-lucide-phone-forwarded',
    ORDER_DETAILS: 'i-lucide-receipt',
  };
  return icons[normalizeButtonType(button)] || 'i-lucide-circle';
};

const buttonLabel = button => {
  return (
    button.text ||
    button.display_text ||
    button.displayText ||
    button.example ||
    t('CONVERSATION.WHATSAPP_TEMPLATE.COPY_CODE')
  );
};

const handleButtonClick = async button => {
  const buttonType = normalizeButtonType(button);
  if (buttonType === 'URL' && button.url) {
    window.open(button.url, '_blank', 'noopener,noreferrer');
  } else if (buttonType === 'PHONE_NUMBER' && button.phone_number) {
    window.open(`tel:${button.phone_number}`, '_self');
  } else if (buttonType === 'VOICE_CALL') {
    const callLink = button.deep_link || button.deepLink || button.url;
    if (callLink) window.open(callLink, '_blank', 'noopener,noreferrer');
  } else if (['COPY_CODE', 'ORDER_DETAILS'].includes(buttonType)) {
    const codeToCopy =
      button.pix_code ||
      button.pixCode ||
      button.copy_code ||
      button.copyCode ||
      button.parameter ||
      button.text;
    if (codeToCopy) {
      try {
        await navigator.clipboard.writeText(codeToCopy);
        useAlert(t('CONVERSATION.WHATSAPP_TEMPLATE.CODE_COPIED'));
      } catch (err) {
        useAlert(t('CONVERSATION.WHATSAPP_TEMPLATE.COPY_ERROR'));
      }
    }
  }
};

const metaAlignClass = computed(() =>
  orientation.value === ORIENTATION.RIGHT ? 'justify-end' : 'justify-start'
);

const metaColorClass = computed(() =>
  variant.value === MESSAGE_VARIANTS.PRIVATE
    ? 'text-n-amber-12/50'
    : 'text-n-slate-11'
);
</script>

<template>
  <div class="flex flex-col max-w-80">
    <BaseBubble
      hide-meta
      class="p-0 overflow-hidden"
      data-bubble-name="whatsapp-template"
    >
      <div class="flex flex-col">
        <!-- Header media -->
        <div v-if="hasMediaHeader && mediaUrl">
          <img
            v-if="header.format === 'IMAGE'"
            :src="mediaUrl"
            alt=""
            class="w-full max-h-44 object-cover rounded-t-lg"
          />
          <video
            v-else-if="header.format === 'VIDEO'"
            :src="mediaUrl"
            controls
            class="w-full max-h-44"
          />
          <a
            v-else-if="header.format === 'DOCUMENT'"
            :href="mediaUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center gap-2 p-3 bg-n-alpha-1 hover:bg-n-alpha-2"
          >
            <span class="i-lucide-file-text size-5 text-n-slate-11" />
            <span class="text-sm text-n-blue-11 truncate">
              {{ t('CONVERSATION.WHATSAPP_TEMPLATE.VIEW_DOCUMENT') }}
            </span>
          </a>
        </div>

        <!-- Text header -->
        <div v-if="hasTextHeader" class="px-3 pt-3 pb-0">
          <p class="text-sm font-semibold text-n-slate-12">
            {{ header.text }}
          </p>
        </div>

        <!-- Body + meta -->
        <div class="px-3 py-2.5">
          <FormattedContent v-if="content" :content="content" />

          <WhatsAppPaymentOrderCard
            v-if="orderDetailsButton"
            :order="orderDetailsButton"
            :is-updating-status="isUpdatingStatus"
            @update-order-status="updateOrderStatus"
          />

          <AttachmentChips :attachments="attachments" class="gap-2 mt-2" />
          <MessageMeta :class="[metaAlignClass, metaColorClass]" class="mt-2" />
        </div>

        <!-- Footer -->
        <div v-if="footer" class="px-3 pb-2.5">
          <p class="text-xs text-n-slate-10">{{ footer }}</p>
        </div>

        <!-- CTA buttons (URL, Phone, Flow, Copy) — inside the bubble -->
        <div v-if="ctaButtons.length" class="border-t border-n-weak">
          <button
            v-for="(button, index) in ctaButtons"
            :key="`cta-${index}`"
            class="flex items-center justify-center gap-1.5 w-full px-3 py-2 text-sm font-medium text-n-blue-11 hover:bg-n-alpha-1 transition-colors"
            :class="{ 'border-t border-n-weak': index > 0 }"
            @click="handleButtonClick(button)"
          >
            <span :class="ctaButtonIcon(button)" class="size-3.5" />
            <span>{{ buttonLabel(button) }}</span>
          </button>
        </div>
      </div>
    </BaseBubble>

    <!-- Quick Reply buttons — separate pills below the bubble -->
    <div v-if="quickReplyButtons.length" class="flex flex-wrap gap-1.5 mt-1.5">
      <button
        v-for="(button, index) in quickReplyButtons"
        :key="`qr-${index}`"
        class="flex-1 min-w-0 px-3 py-2 text-sm font-semibold text-n-blue-11 bg-white dark:bg-n-solid-3 rounded-lg shadow-sm border border-n-strong hover:bg-n-alpha-1 transition-colors text-center truncate"
      >
        {{ button.text }}
      </button>
    </div>
  </div>
</template>
